// Minimal local types to avoid importing FilterContext (which pulls in the full Apollo/React chain).
// Keep in sync with GeoFilter in FilterContext.tsx.
import { GeoFilter } from '~/contexts/FilterContext';
import { createGeoFilterHelper } from '~/utils/filters';

interface GeoResult {
  postalCodes: (string | null)[];
  count: number;
}

interface PostalCodeCountInput {
  postalCode: string;
  count: number;
}

export function buildGeoFiltersFromSnapshot(
  geoFiltersToLoad: GeoFilter[],
  geoResults: GeoResult[],
  postalCodeCounts: PostalCodeCountInput[],
): GeoFilter[] {
  const countByPostalCode: Record<string, number> = postalCodeCounts.reduce((obj, { postalCode, count }) => {
    obj[postalCode] = count;
    return obj;
  }, {} as Record<string, number>);

  const hasPositiveSegmentGeoFilters = geoFiltersToLoad.some((f) => !f.isParent && !f.negate);
  const hasNegativeSegmentGeoFilters = geoFiltersToLoad.some((f) => !f.isParent && f.negate);
  const isSegmentCreation = geoFiltersToLoad.some((f) => f.isParent);

  const resultGeoFilters = geoFiltersToLoad.map((geoFilter, i) => {
    if (hasPositiveSegmentGeoFilters && geoFilter.isParent && !geoFilter.negate) {
      return { ...geoFilter, zipCodes: [], count: geoResults[i].count };
    }
    const result = createGeoFilterHelper(geoFilter).geoFilterFromGeoResult(geoResults[i], countByPostalCode);
    if (!geoFilter.isParent && !geoFilter.negate && !result.zipCodes?.some((z) => z.count > 0)) {
      return { ...result, zipCodes: [] };
    }
    return result;
  });

  if (hasNegativeSegmentGeoFilters && isSegmentCreation) {
    let visibleZipSet: Set<string>;

    if (hasPositiveSegmentGeoFilters) {
      const parentZipSet = new Set(
        geoFiltersToLoad.flatMap((f, i) => (f.isParent && !f.negate ? geoResults[i]?.postalCodes ?? [] : [])),
      );
      visibleZipSet = new Set(
        resultGeoFilters
          .filter((f) => !f.isParent && !f.negate)
          .flatMap((f) => f.zipCodes?.filter((z) => parentZipSet.has(z.zip)).map((z) => z.zip) || []),
      );
    } else {
      visibleZipSet = new Set(
        resultGeoFilters.filter((f) => f.isParent).flatMap((f) => f.zipCodes?.map((z) => z.zip) || []),
      );
    }

    resultGeoFilters.forEach((filter, i) => {
      if (!filter.isParent && filter.negate) {
        resultGeoFilters[i] = {
          ...filter,
          zipCodes: filter.zipCodes?.filter((z) => visibleZipSet.has(z.zip)) || [],
        };
      }
    });
  }

  return resultGeoFilters;
}

/**
 * Pure function for the audience overview map (GlobalAudienceMap).
 * Backend returns geoResults ordered: parent filters first, then segment filters.
 * parentGeoFilters and segmentGeoFilters must already have counts stripped
 * (i.e. output of getGeoFiltersWithoutCounts).
 */
export function buildOverviewGeoFiltersFromSnapshot(
  parentGeoFilters: GeoFilter[],
  segmentGeoFilters: GeoFilter[],
  geoResults: GeoResult[],
  postalCodeCounts: PostalCodeCountInput[],
): GeoFilter[] {
  const countByPostalCode: Record<string, number> = postalCodeCounts.reduce((obj, { postalCode, count }) => {
    obj[postalCode] = count;
    return obj;
  }, {} as Record<string, number>);

  const parentFilterCount = parentGeoFilters.length;

  const resultParentGeoFilters: GeoFilter[] = parentGeoFilters.map((geoFilter, j) => ({
    ...createGeoFilterHelper(geoFilter).geoFilterFromGeoResult(
      geoResults[j] ?? { count: 0, postalCodes: [] },
      countByPostalCode,
    ),
    isParent: true,
  }));

  const resultSegmentGeoFilters: GeoFilter[] = segmentGeoFilters.map((geoFilter, i) =>
    createGeoFilterHelper(geoFilter).geoFilterFromGeoResult(geoResults[parentFilterCount + i], countByPostalCode),
  );

  // Remove parent filters whose id is overridden by a segment filter
  const segmentFilterIds = new Set(resultSegmentGeoFilters.map((f) => f.id));
  const filteredParentGeoFilters = resultParentGeoFilters.filter((f) => !segmentFilterIds.has(f.id));

  const isSegment = parentFilterCount > 0;

  let clippedSegmentGeoFilters = resultSegmentGeoFilters;
  if (isSegment) {
    const hasPositiveSegmentFilters = resultSegmentGeoFilters.some((f) => !f.negate);
    const parentZipSet = new Set(filteredParentGeoFilters.flatMap((f) => f.zipCodes?.map((z) => z.zip) || []));

    // Visible area for clipping negate filters:
    // - Positive segment filters present: zips with actual segment members (count > 0)
    // - Pure negate case: all parent zips

    const flatMapFunction = (f) => f.zipCodes?.filter((z) => z.count > 0).map((z) => z.zip) || [];
    const visibleZipSet = hasPositiveSegmentFilters
      ? new Set(resultSegmentGeoFilters.filter((f) => !f.negate).flatMap(flatMapFunction))
      : parentZipSet;

    clippedSegmentGeoFilters = resultSegmentGeoFilters.map((filter) =>
      filter.negate ? { ...filter, zipCodes: filter.zipCodes?.filter((z) => visibleZipSet.has(z.zip)) || [] } : filter,
    );
  }

  return [...filteredParentGeoFilters, ...clippedSegmentGeoFilters];
}
